import React, { useState, useEffect } from 'react';
import { useAuth } from "../../context/DBContext";
import { useNavigate } from "react-router-dom";
import { UserIcon, EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import firebaseErrorMessages from '../../firebaseError';

const validatePasswordStrength = (password) => {
  if (!password) return { isStrong: false, checks: {} };
  
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  
  return {
    isStrong: Object.values(checks).every(Boolean),
    checks
  };
};

const TermsOfServiceModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
          <XMarkIcon className="h-6 w-6" />
        </button>
        <h3 className="text-lg font-bold mb-4">Terms of Service</h3>
        <div className="max-h-96 overflow-y-auto text-sm text-gray-600">
          {/* Terms content... */}
          <p className="mb-4">
            Welcome to our service. By using our platform, you agree to these terms, which are designed to create a positive experience for all users.
          </p>
          {/* Add the rest of your terms here */}
        </div>
        <button onClick={onClose} className="mt-6 w-full bg-blue-600 text-white rounded-md py-2 text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          Close
        </button>
      </div>
    </div>
  );
};

const SignUpPage = () => {
  const { signupLa } = useAuth();
  const navigate = useNavigate();

  // Form state variables
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [department, setDepartment] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [onBehalf, setOnBehalf] = useState(false);
  const [agreement, setAgreement] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ isStrong: false, checks: {} });

  // Error and loading state
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const allowedDomainPatterns = [
    // "purdue.edu",
    "gov.uk",
    "nhs.net"
  ];

  const handleSubmit = async (e) => {
    // console.log("signup initiated");
    e.preventDefault();
    const newErrors = {};

    // Validate inputs
    if (!firstName.trim()) newErrors.firstName = 'Please input your first name';
    if (!lastName.trim()) newErrors.lastName = 'Please input your last name';
    
    if (!email.trim()) {
      newErrors.email = 'Please input your email';
    } else {
      const emailDomain = email.split('@')[1];
      const isAllowedDomain = allowedDomainPatterns.some(pattern => 
        emailDomain === pattern || emailDomain.endsWith('.' + pattern)
      );
      if (!isAllowedDomain) {
        newErrors.email = `Email domain must be or end with one of the following: ${allowedDomainPatterns.join(', ')}`;
      }
    }
    if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Please input a valid email';
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const strength = validatePasswordStrength(password);
      if (!strength.isStrong) {
        newErrors.password = 'Password must meet all requirements';
      }
    }
    
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    if (password !== confirmPassword) newErrors.confirmPassword = 'The passwords do not match.';
    if (!address.trim()) newErrors.address = 'Please input your address';
    if (!postcode.trim()) newErrors.postcode = 'Please input your postcode';
    if (!agreement) newErrors.agreement = 'You must agree to the terms and conditions.';

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      setLoading(true);
      await signupLa(
        firstName,
        lastName,
        email,
        password,
        address,
        postcode,
        department
      );
      navigate("/emailverification");
    } catch (error) {
      const firebaseErrorCode = error.code;
      const customErrorMessage = error.message;
      setErrors({ general: customErrorMessage });
      // console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordStrength(validatePasswordStrength(e.target.value));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-100 p-3">
    <div className="space-y-4 max-w-2xl w-full">

        {/* Information box */}
        <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 mb-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> As a local authority, your account will be managed differently. Please complete the create your account information, and we'll be in touch to discuss pricing and onboarding.
        </p>
      </div>

      <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-lg shadow-xl p-6 max-w-2xl w-full">
        <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Create Your Account</h2>
        {errors.general && (
          <div className="mb-4 text-center text-red-600 text-sm">
            {errors.general}
          </div>
        )}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="first-name" className="block text-xs font-medium text-gray-700">First Name</label>
                <div className="relative">
                  <input
                    id="first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                    placeholder="John"
                  />
                  <UserIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
                </div>
                {errors.firstName && <p className="text-xs text-red-600">{errors.firstName}</p>}
              </div>
              <div>
                <label htmlFor="last-name" className="block text-xs font-medium text-gray-700">Last Name</label>
                <div className="relative">
                  <input
                    id="last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                    placeholder="Doe"
                  />
                  <UserIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
                </div>
                {errors.lastName && <p className="text-xs text-red-600">{errors.lastName}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-700">Email Address</label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="john@example.com"
                />
                <EnvelopeIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-700">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  className="pl-10 pr-8 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                />
                <LockClosedIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-3 w-3" /> : <EyeIcon className="h-3 w-3" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600">{errors.password}</p>}
              
              {/* Password strength indicator */}
              {password && (
                <div className="mt-1">
                  <div className="flex space-x-1 h-1">
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      passwordStrength.checks.length ? 'bg-blue-200' : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      (passwordStrength.checks.uppercase && passwordStrength.checks.lowercase) ? 'bg-blue-300' : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      passwordStrength.checks.number ? 'bg-blue-400' : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      passwordStrength.checks.special ? 'bg-blue-500' : 'bg-gray-200'
                    }`}></div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {!passwordStrength.isStrong && "Required: "}
                    {!passwordStrength.checks.length && "8+ characters, "}
                    {!passwordStrength.checks.uppercase && "uppercase, "}
                    {!passwordStrength.checks.lowercase && "lowercase, "}
                    {!passwordStrength.checks.number && "number, "}
                    {!passwordStrength.checks.special && "special char"}
                    {passwordStrength.isStrong && "Strong password"}
                  </p>
                </div>
              )}
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-xs font-medium text-gray-700">Confirm Password</label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                />
                <LockClosedIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600">{errors.confirmPassword}</p>}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label htmlFor="address" className="block text-xs font-medium text-gray-700">Address</label>
              <div className="relative">
                <input
                  id="address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="123 Main St"
                />
                <MapPinIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.address && <p className="text-xs text-red-600">{errors.address}</p>}
            </div>
            <div >
              <div>
                <label htmlFor="postal-code" className="block text-xs font-medium text-gray-700">Postal Code</label>
                <input
                  id="postal-code"
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="12345"
                />
                {errors.postcode && <p className="text-xs text-red-600">{errors.postcode}</p>}
              </div>
              {/* <div>
                <label htmlFor="age-group" className="block text-xs font-medium text-gray-700">Age Group</label>
                <select
                  id="age-group"
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7 text-gray-700 bg-white"
                >
                  <option value="">Select</option>
                  <option value="Early years (1-5)">Early years (1-5)</option>
                  <option value="Primary (6-13)">Primary (6-13)</option>
                  <option value="Preparing For Adulthood (14-18)">Preparing For Adulthood (14-18)</option>
                  <option value="Young Adults (18-25)">Young Adults (18-25)</option>
                </select>
                {errors.ageGroup && <p className="text-xs text-red-600">{errors.ageGroup}</p>}
              </div> */}
            </div>

            
            <div>
                <label htmlFor="department" className="block text-xs font-medium text-gray-700">Department</label>
                <input
                  id="department"
                  type="text"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="enter your department"
                />
                {errors.department && <p className="text-xs text-red-600">{errors.department}</p>}
              </div>

            {/* <div className="flex items-center">
              <input
                id="on-behalf"
                type="checkbox"
                className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={onBehalf}
                onChange={(e) => setOnBehalf(e.target.checked)}
              />
              <label htmlFor="on-behalf" className="ml-2 block text-xs text-gray-700">Signing up on behalf of someone else?</label>
            </div> */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  type="checkbox"
                  className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={agreement}
                  onChange={(e) => setAgreement(e.target.checked)}
                />
              </div>
              <div className="ml-2">
                <label htmlFor="terms" className="text-xs text-gray-700">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Terms and Conditions
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>
            {errors.agreement && <p className="text-xs text-red-600">{errors.agreement}</p>}
          </div>

          <div className="col-span-full mt-4">
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-1.5 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white ${
                loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {loading ? 'Signing Up...' : 'Create Account'}
            </button>
          </div>
        </form>

        <p className="mt-4 text-center text-xs text-gray-600">
          Already have an account? <a href="/login" className="font-medium text-blue-600 hover:underline">Sign in</a>
        </p>
      </div>
      </div>
    </div>
  );
};

export default SignUpPage;
